// Sidebar ☰ → page options popover (Figma 2727:686): Edit profile, Settings (today's page), Night mode (today's
// dark-mode switch, On/Off) and Payments (Soon). Tags: personal-options-<edit|settings|night|payments> (spec §5).
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banknote, Moon, Rss, Settings, UserPen } from "lucide-react";
import { useTheme } from "../ThemeProvider";

interface Props {
  onClose(): void;
  onEditProfile(): void;
  onPayments(): void;
}

const ITEM =
  "w-full flex items-center gap-4 h-[52px] px-4 bg-transparent border-0 text-left text-[15px] text-[var(--foreground)] cursor-pointer hover:bg-[var(--bg-hover)]";

export default function PageOptionsMenu({ onClose, onEditProfile, onPayments }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark, setTheme } = useTheme();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div aria-hidden="true" className="fixed inset-0 z-[2000]" onClick={onClose} />
      <div
        role="menu"
        aria-label={t("personal_page_options", "Page options")}
        data-testid="personal-menu"
        className="fixed z-[2000] left-2 right-2 bottom-16 md:right-auto md:left-[84px] md:bottom-16 w-auto md:w-[260px] rounded-2xl bg-[var(--bg-surface)] shadow-xl border border-[var(--border)] py-2"
      >
        <button
          type="button"
          role="menuitem"
          data-testid="personal-options-edit"
          className={ITEM}
          onClick={() => {
            onClose();
            onEditProfile();
          }}
        >
          <UserPen size={20} aria-hidden="true" />
          <span className="flex-1">{t("personal_edit_profile", "Edit profile")}</span>
        </button>
        <button
          type="button"
          role="menuitem"
          data-testid="personal-options-settings"
          className={ITEM}
          onClick={() => {
            onClose();
            navigate("/settings");
          }}
        >
          <Settings size={20} aria-hidden="true" />
          <span className="flex-1">{t("nav.settings", "Settings")}</span>
        </button>
        <button
          type="button"
          role="menuitem"
          data-testid="personal-options-following"
          className={ITEM}
          onClick={() => {
            onClose();
            navigate("/feed/following");
          }}
        >
          <Rss size={20} aria-hidden="true" />
          <span className="flex-1">{t("page_options_following_feed", "Following feed")}</span>
        </button>
        <button
          type="button"
          role="menuitemcheckbox"
          aria-checked={isDark}
          data-testid="personal-options-night"
          className={ITEM}
          onClick={() => setTheme(isDark ? "light" : "dark")}
        >
          <Moon size={20} aria-hidden="true" />
          <span className="flex-1">{t("personal_night_mode", "Night mode")}</span>
          <span className="text-xs text-[var(--foreground-muted)]">{isDark ? t("personal_on", "On") : t("personal_off", "Off")}</span>
        </button>
        <button
          type="button"
          role="menuitem"
          data-testid="personal-options-payments"
          className={ITEM}
          onClick={() => {
            onClose();
            onPayments();
          }}
        >
          <Banknote size={20} aria-hidden="true" />
          <span className="flex-1">{t("personal_payments", "Payments")}</span>
          <span className="text-xs text-[var(--foreground-muted)]">{t("personal_soon", "Soon")}</span>
        </button>
      </div>
    </>
  );
}
